import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useScenario, useScenarioReport } from "../api/queries";
import type { CaseTemplate, Scenario } from "../api/types";
import { SimAttemptsTable } from "./SimAttemptsPage";
import { DailyChart, PctBar, ScenarioActions } from "../components/SimPatient";
import { DOMAIN_LABELS, pct, STATUS_LABELS } from "../lib/sim";
import { Card, Empty, ErrorBanner, Loading, PageHeader, Stat, StatusBadge, Td, Th } from "../components/ui";

type Tab = "progress" | "attempts" | "case";
const TABS: { key: Tab; label: string }[] = [
  { key: "progress", label: "Progress" },
  { key: "attempts", label: "Attempts" },
  { key: "case", label: "Case" },
];

// ?tab=progress|attempts|case
export function SimPatientDetailPage() {
  const id = Number(useParams().id);
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "progress") as Tab;
  const scenario = useScenario(id);
  const navigate = useNavigate();
  const s = scenario.data;

  if (!s) {
    return (
      <div className="p-6">
        <ErrorBanner error={scenario.error} />
        {scenario.isLoading && <Loading />}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-2 text-sm"><Link to="/sim-patients" className="text-blue-700 hover:underline">← Simulated patients</Link></div>
      <PageHeader
        title={s.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={s.status} label={STATUS_LABELS[s.status]} />
            <span>
              v{s.version} · {s.difficulty} · {s.course_name ?? "no course"}
              {s.module_name ? ` · ${s.module_name}` : ""} · {s.decision_point_count} decision points, {s.total_max_score} pts
            </span>
          </span>
        }
        actions={
          <>
            {s.status !== "archived" && (
              <Link to={`/sim-patients/${s.id}/edit`} className="inline-flex items-center rounded bg-white px-3 py-1.5 text-sm font-medium ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Edit</Link>
            )}
            <ScenarioActions scenario={s} onDeleted={() => navigate("/sim-patients")} />
          </>
        }
      />

      <div className="mb-4 flex border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setParams(t.key === "progress" ? {} : { tab: t.key }, { replace: true })}
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${tab === t.key ? "border-gray-900 font-medium" : "border-transparent text-gray-500 hover:text-gray-900"}`}
          >
            {t.label}
            {t.key === "attempts" && <span className="ml-1.5 text-xs text-gray-400">{s.attempt_count}</span>}
          </button>
        ))}
      </div>

      {tab === "progress" && <ProgressTab scenarioId={s.id} />}
      {tab === "attempts" && <Card><SimAttemptsTable scenarioId={s.id} /></Card>}
      {tab === "case" && <CaseTab scenario={s} />}
    </div>
  );
}

function ProgressTab({ scenarioId }: { scenarioId: number }) {
  const report = useScenarioReport(scenarioId);
  const r = report.data;
  if (!r) return <><ErrorBanner error={report.error} />{report.isLoading && <Loading />}</>;
  const a = r.attempts;
  const maxBucket = Math.max(1, ...r.score_distribution.map((b) => b.count));

  return (
    <div className="space-y-4">
      <Card>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <Stat label="Attempts" value={a.started} hint={`${a.unique_learners} learners`} />
          <Stat label="Completed" value={a.completed} hint={`${pct(a.completion_rate)} completion`} />
          <Stat label="In progress" value={a.in_progress} hint={`${a.abandoned} abandoned`} />
          <Stat label="Avg score" value={pct(a.avg_score_pct)} hint="completed attempts" />
          <Stat
            label="Missed a critical point"
            value={pct(r.critical_miss_rate)}
            hint={r.critical_miss_rate === null ? "no critical points" : "of completed attempts"}
          />
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Decision point hit rate" className="lg:row-span-2">
          {!a.completed ? <Empty>No completed attempts yet.</Empty> : (
            <ul className="space-y-3">
              {r.decision_points.map((p) => (
                <li key={p.id}>
                  <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                    <span>
                      {p.description}
                      {p.is_critical && <span className="ml-2 rounded bg-red-50 px-1.5 text-xs text-red-700">critical</span>}
                    </span>
                    <span className="shrink-0 text-xs text-gray-500">{p.hit_count}/{a.completed} · {p.category.replace(/_/g, " ")}</span>
                  </div>
                  <PctBar value={p.hit_rate} warn={p.is_critical && (p.hit_rate ?? 0) < 80} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Score distribution">
          {!a.completed ? <Empty>No completed attempts yet.</Empty> : (
            <div className="flex h-36 items-end gap-1" role="img" aria-label="Score distribution">
              {r.score_distribution.map((b) => (
                <div key={b.label} className="flex h-full flex-1 flex-col items-center justify-end" title={`${b.label}: ${b.count}`}>
                  <span className="mb-1 text-xs tabular-nums text-gray-500">{b.count || ""}</span>
                  <div className="w-full rounded-t bg-gray-800" style={{ height: `${(b.count / maxBucket) * 80}%` }} />
                  <span className="mt-1 text-[10px] text-gray-500">{b.label.split("-")[0]}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Rubric averages">
          {!r.rubric.length ? <Empty>No scored attempts yet.</Empty> : (
            <table className="w-full">
              <thead className="border-b"><tr><Th>Criterion</Th><Th className="text-right">Avg</Th><Th className="w-1/2" /></tr></thead>
              <tbody className="divide-y">
                {r.rubric.map((c) => (
                  <tr key={c.criterion}>
                    <Td>{c.criterion}</Td>
                    <Td className="text-right tabular-nums">{c.avg_score}/{c.max_score}</Td>
                    <Td><PctBar value={c.avg_pct} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Card title="Activity, last 30 days"><DailyChart daily={r.daily} /></Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  if (children === "" || children === null || children === undefined) return null;
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-3 py-1 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="whitespace-pre-wrap">{children}</dd>
    </div>
  );
}

function List({ items }: { items: string[] }) {
  if (!items.length) return <p className="text-sm text-gray-400">None.</p>;
  return <ul className="list-disc space-y-1 pl-5 text-sm">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>;
}

// The generated AI Simulated Patient Case Template, read-only, in template order.
function TemplateView({ t, scenario: s }: { t: CaseTemplate; scenario: Scenario }) {
  const info = t.case_information;
  const p = t.patient_profile;
  const v = p.vital_signs;
  const refs = t.reference_sources;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Case information">
          <dl>
            <Row label="Case ID">SP-{String(s.id).padStart(4, "0")}</Row>
            <Row label="Clinical topic">{info.clinical_topic}</Row>
            <Row label="CPD module">{info.cpd_module}</Row>
            <Row label="Difficulty">{info.difficulty}</Row>
            <Row label="Target learners">{info.target_learners.join(", ")}</Row>
            <Row label="Estimated time">{`${info.estimated_minutes} minutes`}</Row>
          </dl>
          <h3 className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Learning objectives</h3>
          <List items={info.learning_objectives} />
        </Card>
        <Card title="Reference sources">
          <dl>
            <Row label="MoH CPD module">{refs.cpd_module}</Row>
            <Row label="National guideline">{refs.national_guideline}</Row>
            <Row label="Job aid">{refs.job_aid}</Row>
            <Row label="SOP / protocol">{refs.sop_protocol}</Row>
            <Row label="Version / date">{refs.version_date}</Row>
          </dl>
          {t.source_documents.length > 0 && <p className="mt-2 text-xs text-gray-500">Generated from {t.source_documents.length} approved course documents.</p>}
        </Card>
      </div>

      <Card title={`Patient: ${p.name}${p.age !== null ? `, ${p.age}` : ""}${p.sex ? `, ${p.sex}` : ""}`}>
        <div className="grid gap-6 lg:grid-cols-2">
          <dl>
            <Row label="Occupation">{p.occupation}</Row>
            <Row label="Residence">{p.residence}</Row>
            <Row label="Chief complaint">{p.chief_complaint}</Row>
            <Row label="Presenting symptoms">{p.presenting_symptoms.join(", ")}</Row>
            <Row label="History of present illness">{p.history_of_present_illness}</Row>
            <Row label="Past medical history">{p.past_medical_history}</Row>
            <Row label="Medications">{p.medication_history}</Row>
            <Row label="Allergies">{p.allergies}</Row>
            <Row label="Obstetric history">{p.obstetric_history}</Row>
            <Row label="Family / social">{p.family_social_history}</Row>
          </dl>
          <div>
            <dl>
              <Row label="Temperature">{v.temperature}</Row>
              <Row label="Blood pressure">{v.blood_pressure}</Row>
              <Row label="Pulse">{v.pulse}</Row>
              <Row label="Respiratory rate">{v.respiratory_rate}</Row>
              <Row label="SpO₂">{v.oxygen_saturation}</Row>
            </dl>
            <h3 className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Examination findings</h3>
            <List items={p.physical_examination} />
          </div>
        </div>
      </Card>

      <Card title="Case introduction">
        <p className="text-sm">{t.case_introduction}</p>
        <p className="mt-3 text-sm text-gray-600">Patient opens with: “{t.opening_statement}”</p>
      </Card>

      <Card title="Critical decision points">
        <div className="space-y-4">
          {t.critical_decision_points.map((d) => (
            <section key={d.domain}>
              <h3 className="flex justify-between text-sm font-semibold">
                <span>{DOMAIN_LABELS[d.domain]}</span><span className="text-gray-500">{d.score} pts</span>
              </h3>
              {d.expected_action && <p className="text-xs text-gray-500">{d.expected_action}</p>}
              <ul className="mt-1 divide-y rounded border">
                {d.actions.map((a, i) => (
                  <li key={a.id ?? i} className="flex justify-between gap-3 px-3 py-1.5 text-sm">
                    <span>
                      {a.description}
                      {a.is_critical && <span className="ml-2 rounded bg-red-50 px-1.5 text-xs text-red-700">critical</span>}
                    </span>
                    <span className="shrink-0 text-gray-500">{a.points}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Card>

      <Card title={`Branching dialogue (${t.dialogue_steps.length} steps)`}>
        <ol className="space-y-4">
          {t.dialogue_steps.map((step, i) => (
            <li key={i}>
              <p className="text-sm font-medium">Step {i + 1}: “{step.patient_statement}”</p>
              <table className="mt-1 w-full">
                <tbody className="divide-y">
                  {step.options.map((o) => (
                    <tr key={o.key}>
                      <Td className="w-8 align-top font-semibold">{o.key}</Td>
                      <Td className="align-top">{o.text}<div className="text-xs text-gray-500">{o.feedback}</div></Td>
                      <Td className="align-top text-gray-600">“{o.patient_response}”</Td>
                      <Td className={`w-12 text-right align-top tabular-nums ${o.score < 0 ? "text-red-600" : "text-green-700"}`}>{o.score > 0 ? `+${o.score}` : o.score}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </li>
          ))}
        </ol>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Branching logic">
          <ul className="space-y-1 text-sm">
            {t.performance_bands.map((b, i) => (
              <li key={i} className="flex justify-between"><span>{b.level}</span><span className="text-gray-500">{b.min_score}–{b.max_score}</span></li>
            ))}
          </ul>
        </Card>
        <Card title="Key learning points"><List items={t.key_learning_points} /></Card>
        <Card title="Learner reflection"><List items={t.reflection_prompts} /></Card>
      </div>

      <Card title="Scenario metadata">
        <dl className="grid gap-x-6 md:grid-cols-2">
          <Row label="Version">{String(s.version)}</Row>
          <Row label="Author">{s.authored_by_email ?? ""}</Row>
          <Row label="Clinical reviewer (MoH)">{t.metadata.clinical_reviewer || "Not recorded"}</Row>
          <Row label="Technical reviewer">{t.metadata.technical_reviewer || "Not recorded"}</Row>
          <Row label="Approval date">{t.metadata.approval_date}</Row>
          <Row label="Next review date">{t.metadata.next_review_date}</Row>
          <Row label="Status">{STATUS_LABELS[s.status]}</Row>
        </dl>
      </Card>
    </div>
  );
}

function CaseTab({ scenario: s }: { scenario: Scenario }) {
  if (s.case_template) return <TemplateView t={s.case_template} scenario={s} />;
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Card title="Presenting complaint"><p className="whitespace-pre-wrap text-sm">{s.presenting_complaint}</p></Card>
        <Card title="Decision points">
          {!s.decision_points.length ? <Empty>None yet.</Empty> : (
            <ol className="divide-y">
              {s.decision_points.map((p, i) => (
                <li key={p.id} className="py-3 text-sm first:pt-0 last:pb-0">
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">
                      {i + 1}. {p.description}
                      {p.is_critical && <span className="ml-2 rounded bg-red-50 px-1.5 text-xs font-normal text-red-700">critical</span>}
                    </span>
                    <span className="shrink-0 text-gray-500">{p.max_score} pts · {p.category.replace(/_/g, " ")}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {p.trigger_keywords.map((kw) => <code key={kw} className="rounded bg-gray-100 px-1.5 text-xs">{kw}</code>)}
                  </div>
                  {p.reveals && <p className="mt-1 text-gray-600">Reveals: “{p.reveals}”</p>}
                  {p.feedback_if_missed && <p className="mt-1 text-gray-500">If missed: {p.feedback_if_missed}</p>}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
      <div className="space-y-4">
        <Card title="Persona">
          {!Object.keys(s.patient_persona).length ? <Empty>Not set.</Empty> : (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              {Object.entries(s.patient_persona).map(([k, v]) => (
                <div key={k} className="contents"><dt className="text-gray-500">{k.replace(/_/g, " ")}</dt><dd>{String(v)}</dd></div>
              ))}
            </dl>
          )}
        </Card>
        <Card title="Hidden history">
          {!s.hidden_history.length ? <Empty>None.</Empty> : (
            <ul className="space-y-2 text-sm">
              {s.hidden_history.map((h, i) => (
                <li key={i}><span className="text-gray-500">{h.category}:</span> {h.detail}</li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Rubric">
          {!s.rubric.length ? <p className="text-sm text-gray-500">Scored by decision-point category.</p> : (
            <ul className="space-y-1 text-sm">
              {s.rubric.map((r) => (
                <li key={r.criterion} className="flex justify-between"><span>{r.criterion}</span><span className="text-gray-500">{r.max_score ?? "auto"}</span></li>
              ))}
            </ul>
          )}
        </Card>
        <p className="text-xs text-gray-500">Authored by {s.authored_by_email ?? "unknown"}.</p>
      </div>
    </div>
  );
}
